import React, { useEffect, useState } from 'react';
import { supabase } from '../supabase';
import { Modal } from '../../components/Modal';
import { Edit, Trash2 } from 'lucide-react';
import { isProtectedLineItemConfig } from '../utils/lineItemConfigs';
import { analytics } from '../../utils/analytics';

interface LineItemConfig {
  id: string;
  name: string;
  type: string;
  description?: string;
  subtype?: string;
  created_at: string;
}

// Function to check if a line item config is protected
function isProtectedConfig(config: LineItemConfig): boolean {
  return isProtectedLineItemConfig(config);
}

// Prop to allow parent to trigger the 'add' mode
interface AdminLineItemConfigsProps {
  triggerAddMode?: boolean; // Optional: Parent can set this to true to show the form
  onFormClose?: () => void; // Optional: Parent can be notified when form is closed
}

export function AdminLineItemConfigs({ triggerAddMode, onFormClose }: AdminLineItemConfigsProps) {
  const [configs, setConfigs] = useState<LineItemConfig[]>([]);
  const [usedConfigIds, setUsedConfigIds] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isAdding, setIsAdding] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [selectedConfig, setSelectedConfig] = useState<LineItemConfig | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [selectedConfigId, setSelectedConfigId] = useState<string | null>(null);
  const [newConfig, setNewConfig] = useState<Partial<LineItemConfig>>({
    name: '',
    type: 'fee',
    description: '',
    subtype: '',
  });

  useEffect(() => {
    fetchConfigsAndPricingData();
  }, []);

  useEffect(() => {
    if (triggerAddMode) {
      setIsAdding(true);
      setIsEditing(false);
      setSelectedConfig(null);
      setNewConfig({ name: '', type: 'fee', description: '', subtype: '' });
      setFormError(null);
      setSuccessMessage(null);
    }
  }, [triggerAddMode]);

  async function fetchConfigsAndPricingData() {
    setLoading(true);
    try {
      // Fetch all line item configs
      const { data: configsData, error: configsError } = await supabase
        .from('line_item_configs')
        .select('*')
        .order('created_at', { ascending: false });

      if (configsError) throw configsError;
      setConfigs(configsData || []);

      // Fetch all used line_item_config_ids from the pricing table
      const { data: pricingData, error: pricingError } = await supabase
        .from('pricing')
        .select('line_item_config_id');

      if (pricingError) throw pricingError;
      const usedIds = new Set(pricingData?.map(p => p.line_item_config_id).filter(id => id) || []);
      setUsedConfigIds(usedIds);

    } catch (error: unknown) {
      console.error('Error fetching data:', error);
      const errorObj = error as { message?: string };
      setError(errorObj.message || 'An unknown error occurred');
    } finally {
      setLoading(false);
    }
  }

  function handleEditClick(config: LineItemConfig) {
    if (isProtectedConfig(config)) {
      return; // Do nothing if the config is protected
    }
    setSelectedConfig(config);
    setNewConfig({
      name: config.name,
      type: config.type,
      description: config.description || '',
      subtype: config.subtype || '',
    });
    setIsEditing(true);
    setIsAdding(false);
    setFormError(null);
    setSuccessMessage(null);
  }

  function handleCancelEdit() {
    setIsEditing(false);
    setIsAdding(false);
    setSelectedConfig(null);
    setNewConfig({ name: '', type: 'fee', description: '', subtype: '' });
    setFormError(null);
    if (onFormClose) {
      onFormClose(); // Notify parent that form is closed
    }
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setFormError(null);

    try {
      if (!newConfig.name) {
        setFormError('Name is required');
        return;
      }

      if (isEditing && selectedConfig) {
        const { error } = await supabase
          .from('line_item_configs')
          .update({
            name: newConfig.name,
            type: newConfig.type,
            description: newConfig.description || newConfig.name,
            subtype: newConfig.subtype || null,
          })
          .eq('id', selectedConfig.id);

        if (error) throw error;
        analytics.trackAdminAction('admin_line_item_config_update', {
          entityType: 'line_item_config',
          entityId: selectedConfig.id,
          metadata: { name: newConfig.name, type: newConfig.type, subtype: newConfig.subtype || null },
        });
        setSuccessMessage('Line item configuration updated successfully!');
      } else {
        const { error } = await supabase
          .from('line_item_configs')
          .insert([{
            name: newConfig.name,
            type: newConfig.type,
            description: newConfig.description || newConfig.name,
            subtype: newConfig.subtype || null,
          }]);

        if (error) throw error;
        // Shared catalog rows: every program's pricing references these by id, so a change
        // here reaches invoices that have not been generated yet.
        analytics.trackAdminAction('admin_line_item_config_create', {
          entityType: 'line_item_config',
          metadata: { name: newConfig.name, type: newConfig.type, subtype: newConfig.subtype || null },
        });
        setSuccessMessage('Line item configuration added successfully!');
      }

      handleCancelEdit(); // This will also call onFormClose if provided
      fetchConfigsAndPricingData();
    } catch (error: unknown) {
      console.error('Error saving line item config:', error);
      const errorObj = error as { message?: string };
      setFormError(errorObj.message || 'An unknown error occurred');
    }
  }

  async function handleDeleteConfig(configId: string, configName: string) {
    const config = configs.find(c => c.id === configId);
    if (!config || isProtectedConfig(config)) {
      return; // Do nothing if the config is protected or not found
    }

    if (window.confirm(`Are you sure you want to delete the configuration "${configName}"? This action cannot be undone.`)) {
      try {
        setLoading(true);
        const { error: deleteError } = await supabase
          .from('line_item_configs')
          .delete()
          .eq('id', configId);

        if (deleteError) throw deleteError;

        analytics.trackAdminAction('admin_line_item_config_delete', {
          entityType: 'line_item_config',
          entityId: configId,
          metadata: { name: configName },
        });
        setSuccessMessage(`Configuration "${configName}" deleted successfully!`);
        fetchConfigsAndPricingData();
      } catch (error: unknown) {
        console.error('Error deleting line item config:', error);
        const errorObj = error as { message?: string };
        setError(errorObj.message || 'An unknown error occurred while deleting.');
      } finally {
        setLoading(false);
      }
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <p className="text-xl">Loading line item configurations...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="mb-4 p-4 bg-red-50 text-red-600 rounded">
        {error}
      </div>
    );
  }

  return (
    <>
      {successMessage && (
        <div className="m-4 p-4 bg-green-50 text-green-700 rounded-md">
          {successMessage}
        </div>
      )}

      <Modal
        isOpen={isAdding || isEditing}
        onClose={handleCancelEdit}
        title={isEditing ? 'Edit Line Item Configuration' : 'Add New Line Item Configuration'}
      >
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label htmlFor="config-name" className="block text-sm font-medium text-gray-700">
                Name
              </label>
              <input
                type="text"
                id="config-name"
                className="mt-1 focus:ring-blue-500 focus:border-blue-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                value={newConfig.name}
                onChange={(e) => setNewConfig({ ...newConfig, name: e.target.value })}
                required
              />
            </div>

            <div>
              <label htmlFor="config-type" className="block text-sm font-medium text-gray-700">
                Type
              </label>
              <select
                id="config-type"
                className="mt-1 focus:ring-blue-500 focus:border-blue-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                value={newConfig.type}
                onChange={(e) => setNewConfig({ ...newConfig, type: e.target.value })}
              >
                <option value="fee">Fee</option>
                <option value="tuition">Tuition</option>
                <option value="materials">Materials</option>
                <option value="discount">Discount</option>
              </select>
            </div>

            <div>
              <label htmlFor="config-subtype" className="block text-sm font-medium text-gray-700">
                Subtype (Optional)
              </label>
              <input
                type="text"
                id="config-subtype"
                className="mt-1 focus:ring-blue-500 focus:border-blue-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                value={newConfig.subtype || ''}
                onChange={(e) => setNewConfig({ ...newConfig, subtype: e.target.value })}
              />
            </div>

            <div>
              <label htmlFor="config-description" className="block text-sm font-medium text-gray-700">
                Description (Optional)
              </label>
              <input
                type="text"
                id="config-description"
                className="mt-1 focus:ring-blue-500 focus:border-blue-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                value={newConfig.description || ''}
                onChange={(e) => setNewConfig({ ...newConfig, description: e.target.value })}
              />
            </div>
          </div>

          {formError && (
            <div className="text-red-600 text-sm">{formError}</div>
          )}

          <div className="flex justify-end space-x-3">
            <button
              type="button"
              onClick={handleCancelEdit}
              className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
            >
              {isEditing ? 'Update' : 'Add'}
            </button>
          </div>
        </form>
      </Modal>
      {/* Mobile: cards */}
      <div className="sm:hidden space-y-3 p-4">
        {configs.map((config) => (
          <div
            key={config.id}
            className={`bg-white rounded-lg shadow p-4 cursor-pointer ${selectedConfigId === config.id ? 'ring-2 ring-blue-400 bg-blue-50' : ''}`}
            onClick={() => setSelectedConfigId(selectedConfigId === config.id ? null : config.id)}
          >
            <div className="flex items-start justify-between">
              <div className="min-w-0">
                <div className="text-base font-semibold text-gray-900 break-words">{config.name}</div>
                <div className="mt-1 flex flex-wrap items-center gap-2">
                  <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-700">
                    {config.type.charAt(0).toUpperCase() + config.type.slice(1)}
                  </span>
                  {config.subtype && (
                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-700">
                      {config.subtype}
                    </span>
                  )}
                </div>
                {config.description && (
                  <div className="mt-2 text-sm text-gray-600 break-words">{config.description}</div>
                )}
              </div>
            </div>
            {selectedConfigId === config.id && !isProtectedConfig(config) && (
              <div className="flex flex-wrap gap-2 mt-3 pt-3 border-t border-blue-100">
                <button
                  onClick={(e) => { e.stopPropagation(); handleEditClick(config); }}
                  className="inline-flex items-center px-2 py-1 text-xs font-medium text-blue-600 bg-blue-50 border border-blue-200 rounded-md hover:bg-blue-100 hover:text-blue-900 transition-colors"
                  aria-label={`Edit ${config.name}`}
                >
                  <Edit className="h-4 w-4 mr-1" />
                  Edit
                </button>
                {!usedConfigIds.has(config.id) && (
                  <button
                    onClick={(e) => { e.stopPropagation(); handleDeleteConfig(config.id, config.name); }}
                    className="inline-flex items-center px-2 py-1 text-xs font-medium text-red-600 bg-red-50 border border-red-200 rounded-md hover:bg-red-100 hover:text-red-900 transition-colors"
                    aria-label={`Delete ${config.name}`}
                  >
                    <Trash2 className="h-4 w-4 mr-1" />
                    Delete
                  </button>
                )}
              </div>
            )}
          </div>
        ))}
        {configs.length === 0 && (
          <div className="text-sm text-gray-500 text-center py-6">No line item configurations found</div>
        )}
      </div>

      {/* Desktop: table */}
      <div className="hidden sm:block overflow-x-auto overflow-y-auto lg:flex-1 lg:min-h-0">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50 sticky top-0 z-10">
            <tr>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Name
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Type
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Subtype
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Description
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {configs.map((config) => (
              <React.Fragment key={config.id}>
                <tr
                  className={selectedConfigId === config.id ? 'bg-blue-50 cursor-pointer' : 'hover:bg-gray-50 cursor-pointer'}
                  onClick={() => setSelectedConfigId(selectedConfigId === config.id ? null : config.id)}
                >
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">{config.name}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-500">
                      {config.type.charAt(0).toUpperCase() + config.type.slice(1)}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-500">{config.subtype || '-'}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-500">{config.description || '-'}</div>
                  </td>
                </tr>
                {selectedConfigId === config.id && !isProtectedConfig(config) && (
                  <tr className="bg-blue-50">
                    <td colSpan={4} className="px-6 py-2 border-b border-blue-100">
                      <div className="flex flex-wrap gap-2">
                        <button
                          onClick={(e) => { e.stopPropagation(); handleEditClick(config); }}
                          className="inline-flex items-center px-2 py-1 text-xs font-medium text-blue-600 bg-blue-50 border border-blue-200 rounded-md hover:bg-blue-100 hover:text-blue-900 transition-colors"
                        >
                          <Edit className="h-3 w-3 mr-1" />
                          Edit
                        </button>
                        {!usedConfigIds.has(config.id) && (
                          <button
                            onClick={(e) => { e.stopPropagation(); handleDeleteConfig(config.id, config.name); }}
                            className="inline-flex items-center px-2 py-1 text-xs font-medium text-red-600 bg-red-50 border border-red-200 rounded-md hover:bg-red-100 hover:text-red-900 transition-colors"
                          >
                            <Trash2 className="h-3 w-3 mr-1" />
                            Delete
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
} 